import { ConstantItems, getDefaultConfig, Constants } from "@constants/shared";
import * as _ from "lodash";

/** Options for client search; activeStatusFilter "Active" | "Inactive" | "All" takes precedence over displayActiveClients when set. */
export interface ClientSearchOptions {
  page?: string;
  itemsPerPage?: string;
  searchText?: string;
  searchColumn?: string;
  orderColumn?: string;
  orderDirection?: string;
  displayAllClients?: boolean;
  selectedInvestmentOfficers?: Array<string>;
  selectedPortfolioManagers?: Array<string>;
  selectedAdministrativeOfficers?: Array<string>;
  activeStatusFilter?: string;
  displayActiveClients?: boolean;
  displayInstitutions?: boolean;
  displayIndividuals?: boolean;
  selectedGeographicalLocations?: Array<number>;
  selectedProfessionalActivities?: Array<number>;
  selectedSecurityRestrictions?: Array<number>;
  hasTradeRestrictions?: boolean;
}

export class ClientRepo {
  static $inject = ["$http", "config"];

  constructor(
    private $http: angular.IHttpService,
    private config: ConstantItems
  ) {}

  getClient(clientNumber: string, isActive?: boolean) {
    let config = getDefaultConfig();
    config.params = {
      isActive: isActive,
    };
    return this.$http.get(`${this.config.clientUrl}/${clientNumber}`, config);
  }

  getClientReportingName(clientNumber: string) {
    return this.$http.get(
      `${this.config.clientUrl}/${clientNumber}/reportingName`
    );
  }


  searchClients(options: ClientSearchOptions = {}) {
    let config = getDefaultConfig();
    return this.$http.post(`${this.config.clientUrl}/search`, options, config);
  }

  getClientsForSelection() {
    let config = getDefaultConfig();
    return this.$http.get(`${this.config.clientUrl}/selection`, config);
  }

  getUserClients() {
    return this.$http.get(`${this.config.clientUrl}/UserClients`);
  }

  getClientsForUser(userId: number) {
    return this.$http.get(`${this.config.clientUrl}/users/${userId}`);
  }

  getAllClients() {
    return this.$http.get(`${this.config.clientUrl}/AllClients`);
  }

  getAllRelationshipManagers() {
    return this.$http.get(`${this.config.clientUrl}/AllRelationshipManagers`);
  }

  refreshClients(
    page: string,
    itemsPerPage: string,
    searchText: string,
    searchColumn: string,
    orderColumn: string,
    orderDirection: string,
    displayAllClients: boolean,
    selectedInvestmentOfficers: Array<string>,
    selectedPortfolioManagers: Array<string>,
    selectedAdministrativeOfficers: Array<string>,
    selectedGeographicalLocations: Array<number>,
    selectedProfessionalActivities: Array<number>,
    selectedSecurityRestrictions: Array<number>,
    hasTradeRestrictions: boolean = false,
    displayActive: boolean = false,
    displayInstitutions: boolean = true,
    displayIndividuals: boolean = true
  ) {
    let queryParams = {
      page: page,
      itemsPerPage: itemsPerPage,
      searchText: searchText,
      searchColumn: searchColumn,
      orderColumn: orderColumn,
      orderDirection: orderDirection,
      displayAllClients: displayAllClients.toString(),
      selectedInvestmentOfficers: selectedInvestmentOfficers,
      selectedPortfolioManagers: selectedPortfolioManagers,
      selectedAdministrativeOfficers: selectedAdministrativeOfficers,
      selectedGeographicalLocations: selectedGeographicalLocations,
      selectedProfessionalActivities: selectedProfessionalActivities,
      selectedSecurityRestrictions: selectedSecurityRestrictions,
      hasTradeRestrictions: hasTradeRestrictions,
      displayActiveClients: displayActive,
      displayInstitutions: displayInstitutions,
      displayIndividuals: displayIndividuals,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.get(`${this.config.clientRefreshUrl}`, config);
  }

  filterAccounts(searchText: string, clientId?: number) {
    var params = "searchText=" + searchText;
    if (clientId) {
      params += "&clientNumber=" + clientId;
    }
    return this.$http.get(`${this.config.accountsUrl}/${clientId}?${params}`);
  }

  // Generate the argument string for the request:
  private generateArguments(...args: Array<string>) {
    var parameterString = "";

    _.forEach(args, (arg) => {
      if (!_.isNull(arg) && !_.isEmpty(arg)) {
        if (_.isEmpty(parameterString)) parameterString = "?";
        else parameterString += "&";

        parameterString += arg;
      }
    });
    return parameterString;
  }

  getLatestPVDates() {
    return this.$http.get(`${this.config.clientUrl}/latestDataFeeds`);
  }

  getLatestGenericPVDates(latestDate) {
    var bucket = Constants.getBucket("generic");
    return this.$http.get(
      `${this.config.clientUrl}/latestGenericDataFeeds/${bucket}/${latestDate}`
    );
  }

  getClientFees(clientId: number) {
    let queryParams = {
      clientId: clientId,
    };

    let config = getDefaultConfig();
    config.params = queryParams;
    return this.$http.get(`${this.config.feesUrl}/clients`, config);
  }

  editPortfolioFees = (portfolioIds: Array<number>, portfolioFee: any) => {
    let queryParams = {
      portfolioIds: portfolioIds,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.put(
      `${this.config.feesUrl}/portfolios`,
      portfolioFee,
      config
    );
  };

  addPortfolioFees = (portfolioIds: Array<number>, portfolioFee: any) => {
    let queryParams = {
      portfolioIds: portfolioIds,
    };

    let config = getDefaultConfig();
    config.params = queryParams;

    return this.$http.post(
      `${this.config.feesUrl}/portfolios`,
      portfolioFee,
      config
    );
  };

  modifyDietz = (clientId: number, dietz: number) => {
    let data = {
      clientId,
      dietz
    };

    let config = getDefaultConfig();
    config.params = {}

    return this.$http.post(
      `${this.config.clientUrl}/modify-dietz`,
      data,
      config
    );
  };

  getFeeTypes = () => {
    return this.$http.get(`${this.config.feesUrl}/feetypes`);
  };

  transferPortfolio = (payload: {
    oldClientId: number;
    newClientId: number;
    portfolioId: number;
  }) => {
    return this.$http.put(
      `${this.config.clientUrl}/transferportfolio`,
      payload
    );
  };

  getAvailableExportColumns() {
    let config = getDefaultConfig();
    return this.$http.get(`${this.config.clientUrl}/export/columns`, config);
  }

  exportClientsToExcel(exportRequest: {
    selectedColumns: Array<string>;
    exportAllClients?: boolean;
    filterParams?: any;
  }) {
    let config = getDefaultConfig();
    config.responseType = 'blob'; // Important: expect binary response
    return this.$http.post(
      `${this.config.clientUrl}/export/excel`,
      exportRequest,
      config
    );
  }
}



// WEBPACK FOOTER //
// ./src/services/clients/client.repo.ts